// Renders the reminder editor's markdown subset into preview blocks with `${token}` merge fields
// substituted by sample values. Pure so the parsing is unit-testable without mounting the preview.
//
// This is deliberately NOT a general markdown renderer: the message editor only produces what its
// toolbar offers (bold, italic, links, bullet/ordered lists), so those plus merge tokens are the
// whole grammar. Anything else passes through as plain text.

/** One styled span of text within a preview line. */
export interface PreviewRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  /** Substituted merge-field sample value, highlighted so authors see where fields land. */
  mergeField?: boolean;
  /** Present when the run is a link's visible text. */
  href?: string;
}

export interface PreviewBlock {
  kind: "paragraph" | "bullet" | "ordered";
  /** One entry per line: per list item, or per hard-broken line within a paragraph. */
  lines: PreviewRun[][];
}

/**
 * The sample booking every preview renders with. Keys must be real backend tokens — the preview
 * substitutes whatever the API's merge-field list carries, and this map supplies the values.
 */
const SAMPLE_BOOKING: Record<string, string> = {
  "${first_name}": "Priya",
  "${email}": "priya@raman.co",
  "${order_id}": "9f3c2a10",
  "${order_confirmation_id}": "FLX-48213",
  "${partner_name}": "Nest Collective",
  "${product_id}": "st-2",
  "${space_name}": "Studio 2",
  "${product_category}": "Meeting Room",
  "${booking_date}": "July 30, 2026",
  "${booking_start_time}": "9:00 AM",
  "${booking_end_time}": "5:00 PM",
  "${location_id}": "mission-bay",
  "${location_name}": "Mission Bay",
  "${location_full_address}": "410 Illinois St, San Francisco, CA 94158",
  "${location_directions}":
    "Entrance on the north side; take the stairs to level 2.",
  // Guest confirmation tokens: the subject/title placeholder set and the body's order link.
  "${confirmation_id}": "FLX-48213",
  "${order_company_name}": "Raman & Co",
  "${order_confirmation_link}": "https://book.example.com/orders/9f3c2a10",
  "${cancellation_policy}":
    "Cancel for free up to 48 hours before your booking.",
};

const TOKEN_RE = /\$\{[a-z_]+\}/g;

/** "${first_name}" -> "First name" — the short chip label derived from the token itself. */
export function mergeFieldShortLabel(token: string): string {
  const inner = token.replace(/^\$\{|\}$/g, "").replace(/_/g, " ");
  return inner.charAt(0).toUpperCase() + inner.slice(1);
}

function substituteToken(
  token: string,
  sample: Record<string, string>
): string {
  return sample[token] ?? token;
}

// Inline grammar, longest-match first: bold, italic, link, token. Italic uses single `*` only
// after bold has been consumed, mirroring how tiptap-markdown serializes marks.
const INLINE_RE =
  /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\)|\$\{[a-z_]+\})/g;

function parseInline(
  text: string,
  sample: Record<string, string>
): PreviewRun[] {
  const runs: PreviewRun[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  INLINE_RE.lastIndex = 0;

  const pushPlain = (plain: string) => {
    if (!plain) return;
    // Tokens can appear inside what the outer pass treats as plain text (e.g. inside link labels
    // handled below), so plain segments still get token substitution.
    let cursor = 0;
    TOKEN_RE.lastIndex = 0;
    let tokenMatch: RegExpExecArray | null;
    while ((tokenMatch = TOKEN_RE.exec(plain)) !== null) {
      if (tokenMatch.index > cursor)
        runs.push({ text: plain.slice(cursor, tokenMatch.index) });
      runs.push({
        text: substituteToken(tokenMatch[0], sample),
        mergeField: true,
      });
      cursor = TOKEN_RE.lastIndex;
    }
    if (cursor < plain.length) runs.push({ text: plain.slice(cursor) });
  };

  while ((match = INLINE_RE.exec(text)) !== null) {
    if (match.index > last) pushPlain(text.slice(last, match.index));
    const tok = match[0];
    if (tok.startsWith("**")) {
      runs.push({ text: fillTokens(tok.slice(2, -2), sample), bold: true });
    } else if (tok.startsWith("${")) {
      runs.push({ text: substituteToken(tok, sample), mergeField: true });
    } else if (tok.startsWith("[")) {
      const split = tok.indexOf("](");
      runs.push({
        text: fillTokens(tok.slice(1, split), sample),
        href: fillTokens(tok.slice(split + 2, -1), sample),
      });
    } else {
      runs.push({ text: fillTokens(tok.slice(1, -1), sample), italic: true });
    }
    last = INLINE_RE.lastIndex;
  }
  if (last < text.length) pushPlain(text.slice(last));
  if (runs.length === 0) runs.push({ text: " " });
  return runs;
}

/** Plain string substitution for contexts that can't carry runs (subject line, link hrefs). */
export function fillTokens(
  text: string,
  sample: Record<string, string> = SAMPLE_BOOKING
): string {
  return text.replace(TOKEN_RE, (token) => substituteToken(token, sample));
}

const BULLET_LINE = /^[-*]\s+/;
const ORDERED_LINE = /^\d+\.\s+/;

// A line break is stored as a literal `<br>`, the one form the mailer's renderer
// carries through. It swallows a newline behind it so the break doesn't also leave
// the trailing space that a soft newline would.
const HARD_BREAK = /<br\s*\/?>\n?/i;

/**
 * Splits markdown into preview blocks. Blocks are separated by blank lines; consecutive `- ` /
 * `1. ` lines fold into one list block so the preview mirrors what the mailer renders.
 */
export function parseEmailPreview(
  markdown: string,
  sample: Record<string, string> = SAMPLE_BOOKING
): PreviewBlock[] {
  const blocks: PreviewBlock[] = [];

  for (const raw of markdown.split(/\n{2,}/)) {
    const segment = raw.trim();
    if (!segment) continue;

    const lines = segment.split("\n");
    if (lines.every((l) => BULLET_LINE.test(l.trim()))) {
      blocks.push({
        kind: "bullet",
        lines: lines.map((l) =>
          parseInline(l.trim().replace(BULLET_LINE, ""), sample)
        ),
      });
    } else if (lines.every((l) => ORDERED_LINE.test(l.trim()))) {
      blocks.push({
        kind: "ordered",
        lines: lines.map((l) =>
          parseInline(l.trim().replace(ORDERED_LINE, ""), sample)
        ),
      });
    } else {
      // Single newlines inside a paragraph render as spaces and an explicit `<br>`
      // breaks the line, both matching HTML email output.
      blocks.push({
        kind: "paragraph",
        lines: segment
          .split(HARD_BREAK)
          .map((line) => parseInline(line.split("\n").join(" "), sample)),
      });
    }
  }

  return blocks;
}
